import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MessageSquare, Vote, Star, ArrowLeft, Loader2, Mic } from "lucide-react";
import api from "@/lib/apiClient";
import { QuestionsPanel, PollsPanel, FeedbackPanel } from "@/components/attendee/panels";

const tabs = [
  { key: "questions", label: "Q&A", icon: MessageSquare },
  { key: "polls", label: "Polls", icon: Vote },
  { key: "feedback", label: "Feedback", icon: Star },
];

export default function AttendeeSession() {
  const { eventId, sessionId } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("questions");

  useEffect(() => { api.get(`/attendee/event/${eventId}`).then(({ data }) => setData(data)).catch(() => setData(false)); }, [eventId]);

  if (data === null) return <div className="min-h-screen flex items-center justify-center bg-muted/30"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (data === false) return <div className="min-h-screen flex items-center justify-center bg-muted/30 p-8 text-center text-muted-foreground">Session not found.</div>;

  const session = data.sessions.find((s) => s.id === sessionId);

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col max-w-md mx-auto">
      <header className="bg-primary text-primary-foreground px-4 pt-5 pb-4 sticky top-0 z-10">
        <Link to={`/attendee/${eventId}`} className="inline-flex items-center gap-1.5 text-xs text-white/70 mb-3" data-testid="attendee-back"><ArrowLeft className="h-3.5 w-3.5" /> {data.event.name}</Link>
        <p className="text-[10px] uppercase tracking-[0.16em] text-accent font-bold">{session?.status === "live" ? "● Live session" : "Session"}</p>
        <h1 className="font-display text-2xl font-bold leading-tight mt-0.5">{session?.title || "Session"}</h1>
        {(session?.start_time || session?.end_time) && <p className="text-xs text-white/60 mt-1">{[session.start_time, session.end_time].filter(Boolean).join(" – ")}</p>}
        {session?.speaker && <p className="text-sm text-white/80 mt-0.5 flex items-center gap-1.5"><Mic className="h-3.5 w-3.5" /> {session.speaker}</p>}
      </header>

      <main className="flex-1 px-4 py-4 pb-24">
        {tab === "questions" && <QuestionsPanel eventId={eventId} sessionId={sessionId} />}
        {tab === "polls" && <PollsPanel eventId={eventId} sessionId={sessionId} />}
        {tab === "feedback" && <FeedbackPanel eventId={eventId} sessionId={sessionId} />}
      </main>

      <nav className="fixed bottom-0 inset-x-0 max-w-md mx-auto bg-card border-t border-border grid grid-cols-3 z-20">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`attendee-session-tab-${t.key}`}
            className={`flex flex-col items-center gap-1 py-3 transition-colors ${tab === t.key ? "text-accent" : "text-muted-foreground"}`}>
            <t.icon className="h-5 w-5" /><span className="text-[10px] font-medium">{t.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
